import { useState, useRef, useEffect } from 'react';
import { UserCircle, Edit3, Loader2, Phone, Mail, Briefcase, MapPin, CreditCard, CheckCircle, Shield, User, Building2, Save, X, FileText, AlertTriangle } from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { User as UserType } from '../types/profile';
import { PageContainer } from '../components/layout/PageContainer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { Link } from 'react-router-dom';
import userApiService from '../services/userApi';

// Fonction d'aide pour obtenir le profil initial depuis localStorage
function getInitialUserProfile(): Partial<UserType> {
  const stored = localStorage.getItem('auth0_user');
  if (!stored) return {};
  
  try {
    const parsedData = JSON.parse(stored);
    return {
      ...parsedData,
      // Mapper sub à id pour être conforme à notre interface User
      id: parsedData.sub || parsedData.id || '',
      // Extraire le rôle des formats potentiels d'Auth0
      role: (parsedData.roles && parsedData.roles[0]) || 
            parsedData['https://ksuit.app/roles']?.[0] || 
            parsedData.role || 
            ''
    };
  } catch (e) {
    console.error('Erreur lors de la lecture des données utilisateur:', e);
    return {};
  }
}

// Fonction pour afficher le statut de vérification d'identité
function IdStatusBadge({ status }: { status?: 'pending' | 'verified' | 'rejected' }) {
  if (!status) return <span className="text-gray-500">Non spécifié</span>;
  
  switch (status) {
    case 'verified':
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" /> Vérifié
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          En attente
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Rejeté
        </span>
      );
    default:
      return <span className="text-gray-500">Non spécifié</span>;
  }
}

export default function Profile() {
  // Utiliser le hook useUser pour les opérations liées au profil
  const {
    user,
    isLoading,
    updateProfile,
    uploadProfilePhoto,
    error
  } = useUser();
  
  // États du profil
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialProfile] = useState(() => getInitialUserProfile());
  const [photo, setPhoto] = useState<string | null>(initialProfile.picture || null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // États pour les conditions d'utilisation
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isLoadingTermsAcceptance, setIsLoadingTermsAcceptance] = useState(false);

  // Champs du profil (initialisés avec les données locales)
  const [formData, setFormData] = useState({
    name: initialProfile.name || '',
    email: initialProfile.email || '',
    phone: initialProfile.phone || '',
    role: initialProfile.role || '',
    address: initialProfile.address || '',
    idNumber: initialProfile.idNumber || '',
    idStatus: initialProfile.idStatus as 'pending' | 'verified' | 'rejected' | undefined || undefined,
  });

  // Mettre à jour les champs lorsque user change (enrichissement depuis le backend)
  useEffect(() => {
    if (user) {
      setFormData(prevFields => ({
        name: user.name || prevFields.name,
        email: user.email || prevFields.email,
        phone: user.phone || prevFields.phone,
        role: user.role || prevFields.role,
        address: user.address || prevFields.address,
        idNumber: user.idNumber || prevFields.idNumber,
        idStatus: user.idStatus || undefined,
      }));
      if (user.picture) {
        setPhoto(user.picture);
      }
    }
  }, [user]);

  // Vérifier les conditions d'utilisation depuis le backend
  useEffect(() => {
    if (user?.termsAccepted) {
      setHasAcceptedTerms(true);
    }
  }, [user]);

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // Restaurer les données originales
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || '',
        address: user.address || '',
        idNumber: user.idNumber || '',
        idStatus: user.idStatus || undefined,
      });
      setPhoto(user.picture || null);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    if (!isEditing) return;
    
    // Traitement spécial pour idStatus pour respecter le type
    if (field === 'idStatus') {
      setFormData(prev => ({
        ...prev,
        idStatus: value === '' ? undefined : value as 'pending' | 'verified' | 'rejected'
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    if (file) {
      // Prévisualisation locale
      const reader = new FileReader();
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        if (ev.target && typeof ev.target.result === 'string') setPhoto(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  }

  const handleSaveProfile = async () => {
    if (!isEditing) return;
    
    setIsSaving(true);
    try {
      // Si un nouveau fichier photo a été sélectionné, l'envoyer au serveur
      if (fileInputRef.current?.files?.length) {
        const file = fileInputRef.current.files[0];
        await uploadProfilePhoto(file);
      } else if (photo !== user?.picture) {
        // Si l'URL de la photo a été changée mais qu'aucun fichier n'a été sélectionné
        await updateProfile({ picture: photo || undefined });
      }
      
      // Mettre à jour les données du profil
      await updateProfile(formData);
      
      // Désactiver le mode édition
      setIsEditing(false);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du profil:', err);
      // Dans un environnement de production, on afficherait un message d'erreur à l'utilisateur
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptTerms = async () => {
    setIsLoadingTermsAcceptance(true);
    try {
      // Persister l'acceptation au backend
      await userApiService.acceptTerms('1.0');
      
      // Mettre à jour l'état local
      setHasAcceptedTerms(true);
    } catch (err) {
      console.error('Erreur lors de l\'acceptation des conditions:', err);
    } finally {
      setIsLoadingTermsAcceptance(false);
    }
  };

  // Afficher un skeleton inline pendant le chargement (pas de loader bloquant)
  if (!user && !initialProfile.email) {
    const hasToken = localStorage.getItem('auth0_token');
    if (hasToken && isLoading) {
      return (
        <PageContainer>
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Profile Header Skeleton */}
            <div className="flex items-center space-x-6">
              <div className="h-24 w-24 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              </div>
            </div>
            {/* Profile Content Skeleton */}
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        </PageContainer>
      );
    }
    
    return (
      <PageContainer>
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="flex items-center space-x-6">
            <div className="h-24 w-24 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (error && !initialProfile.email) {
    // Afficher plus d'informations de debug en mode développement
    const debugInfo = import.meta.env.DEV ? {
      hasToken: !!localStorage.getItem('auth0_token'),
      hasAuth0User: !!localStorage.getItem('auth0_user'),
      backendUrl: import.meta.env.VITE_API_URL,
      errorDetails: error
    } : {};

    return (
      <PageContainer>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 my-8 max-w-3xl mx-auto">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error || "Impossible de charger les informations du profil."}
              </p>
              {import.meta.env.DEV && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 cursor-pointer">Informations de debug</summary>
                  <pre className="text-xs text-red-600 mt-1 whitespace-pre-wrap">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Données à afficher (priorité aux données enrichies du backend, sinon localStorage)
  const displayData = user || initialProfile;
  const displayName = formData.name || displayData.name || 'Utilisateur';
  
  const dataSource = isLoading 
    ? 'Enrichissement des données en cours...' 
    : (user && user.id !== initialProfile.id)
      ? 'Profil enrichi avec les données du backend' 
      : 'Profil basé sur les données locales';

  // Ne JAMAIS afficher le modal d'acceptation tant que `user` n'est pas chargé
  // depuis le backend — sinon l'écran d'acceptation flashe à chaque mount,
  // même si l'utilisateur a déjà accepté (puisque `hasAcceptedTerms` part de false).
  const termsAlreadyAccepted = hasAcceptedTerms || user?.termsAccepted === true;
  const userLoadedFromBackend = !!user && user.id !== initialProfile.id;

  // Si l'utilisateur n'a pas accepté les conditions, afficher l'avertissement
  if (userLoadedFromBackend && !termsAlreadyAccepted) {
    return (
      <PageContainer>
        <div className="py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <div className="mb-6">
                <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                  Acceptation des Conditions Requise
                </h1>
                <p className="text-gray-600">
                  Pour utiliser la plateforme Wanzo et accéder aux fonctionnalités de gestion
                  d'entreprise et d'institution financière, vous devez d'abord accepter nos
                  conditions générales d'utilisation.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Ce que comprend l'acceptation :</h3>
                <ul className="text-left text-blue-800 space-y-1">
                  <li>Respect des conditions d'utilisation de Wanzo</li>
                  <li>Acceptation de notre politique de confidentialité</li>
                  <li>Engagement à fournir des informations véridiques</li>
                  <li>Conformité aux réglementations congolaises et OHADA</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/terms"
                  target="_blank"
                  className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-[#197ca8] bg-white border border-[#197ca8] rounded-md hover:bg-[#197ca8]/5 transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Lire les conditions
                </Link>
                <button
                  onClick={handleAcceptTerms}
                  disabled={isLoadingTermsAcceptance}
                  className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-[#197ca8] rounded-md hover:bg-[#156a92] transition-colors disabled:opacity-50"
                >
                  {isLoadingTermsAcceptance ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Acceptation...
                    </>
                  ) : (
                    'J\'accepte les conditions'
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                En cliquant sur « J'accepte les conditions », vous confirmez avoir lu et accepté
                nos <Link to="/terms" className="underline hover:text-[#197ca8]">Conditions Générales d'Utilisation</Link>.
              </p>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="py-4 sm:py-8">
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
          {/* En-tête avec boutons d'édition - Style inspiré des formulaires d'entreprise */}
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6 pb-4 border-b border-gray-200">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                Profil Administrateur
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Informations personnelles et identification de l'administrateur du compte
              </p>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {!isEditing ? (
                <button
                  onClick={handleStartEditing}
                  className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors"
                >
                  <Edit3 className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Éditer le profil</span>
                  <span className="sm:hidden">Éditer</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancelEditing}
                    disabled={isSaving}
                    className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Annuler</span>
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="inline-flex items-center px-3 py-2 sm:px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1 sm:mr-2" />
                    )}
                    {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Tabs pour organiser le contenu - Style inspiré des formulaires */}
          <Tabs defaultValue="general">
            <TabsList className="mb-8">
              <TabsTrigger value="general" className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Informations Générales
              </TabsTrigger>
              <TabsTrigger value="identification" className="flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Identification & Sécurité
              </TabsTrigger>
            </TabsList>

            {/* Onglet Informations Générales */}
            <TabsContent value="general">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
                {/* Photo et informations principales */}
                <div className="xl:col-span-1">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Photo de profil</h3>
                    
                    <div className="flex flex-col items-center">
                      <div className="relative group mb-4">
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white p-1 border border-gray-200">
                          {photo ? (
                            <img 
                              src={photo} 
                              alt={`Photo de ${displayName}`} 
                              className="w-full h-full rounded-full object-cover" 
                            />
                          ) : (
                            <div className="w-full h-full rounded-full bg-gray-100 flex items-center justify-center">
                              <UserCircle className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {isEditing && (
                          <button
                            type="button"
                            className="absolute inset-0 flex items-center justify-center bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            title="Changer la photo"
                          >
                            <Edit3 size={24} />
                            <input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={handlePhotoChange} />
                          </button>
                        )}
                      </div>
                      
                      <h4 className="text-lg sm:text-xl font-semibold text-gray-900 text-center">{displayName}</h4>
                      <div className="mt-2 flex flex-wrap justify-center gap-2">
                        {formData.role && (
                          <span className="inline-flex rounded-full bg-[#197ca8] bg-opacity-10 px-3 py-1 text-sm font-medium text-[#197ca8]">
                            {formData.role}
                          </span>
                        )}
                        <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                          Administrateur
                        </span>
                      </div>
                      
                      <div className="mt-4 w-full">
                        <div className="text-center">
                          <IdStatusBadge status={formData.idStatus} />
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">
                          {dataSource}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations de contact */}
                <div className="xl:col-span-2">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-6">Informations de contact</h3>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <User className="w-4 h-4 mr-2" />
                            Nom complet
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => handleFieldChange('name', e.target.value)}
                              placeholder="Nom et prénom"
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                              {formData.name || 'Non spécifié'}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <Mail className="w-4 h-4 mr-2" />
                            Adresse email
                          </label>
                          {isEditing ? (
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleFieldChange('email', e.target.value)}
                              placeholder="exemple@email.com"
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                              {formData.email || 'Non spécifié'}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <Phone className="w-4 h-4 mr-2" />
                            Numéro de téléphone
                          </label>
                          {isEditing ? (
                            <input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => handleFieldChange('phone', e.target.value)}
                              placeholder="+243 XXX XXX XXX"
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                              {formData.phone || 'Non spécifié'}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <Briefcase className="w-4 h-4 mr-2" />
                            Fonction / Rôle
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={formData.role}
                              onChange={(e) => handleFieldChange('role', e.target.value)}
                              placeholder="Directeur Général, CEO, etc."
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                              {formData.role || 'Non spécifié'}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <MapPin className="w-4 h-4 mr-2" />
                            Adresse physique
                          </label>
                          {isEditing ? (
                            <textarea
                              value={formData.address}
                              onChange={(e) => handleFieldChange('address', e.target.value)}
                              placeholder="Adresse complète"
                              rows={3}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md min-h-[60px]">
                              {formData.address || 'Non spécifié'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Rôle et Responsabilités */}
              <div className="grid grid-cols-1 gap-6 lg:gap-8 mt-6 lg:mt-8">
                <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-[#197ca8]" />
                    Privilèges d'Administrateur
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Gestion complète du profil d'entreprise
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Accès aux formulaires d'institutions financières
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Configuration des paramètres organisationnels
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Gestion des abonnements et facturation
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">Responsabilités Importantes</h4>
                    <p className="text-sm text-yellow-700">
                      En tant qu'administrateur principal, vous êtes responsable de maintenir 
                      les informations organisationnelles à jour et de respecter les conditions 
                      d'utilisation de la plateforme.
                    </p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800">Conditions d'Utilisation</h4>
                          <p className="text-xs text-green-700">Acceptées{user?.termsAcceptedAt ? ` le ${new Date(user.termsAcceptedAt).toLocaleDateString('fr-FR')}` : ''}</p>
                        </div>
                      </div>
                      <Link
                        to="/terms"
                        target="_blank"
                        className="text-xs text-green-600 hover:text-green-800 underline"
                      >
                        Consulter
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Onglet Identification */}
            <TabsContent value="identification">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-[#197ca8]" />
                  Identification et Vérification
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Numéro de pièce d'identité
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.idNumber}
                          onChange={(e) => handleFieldChange('idNumber', e.target.value)}
                          placeholder="Numéro CNI, Passeport, etc."
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                        />
                      ) : (
                        <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded-md">
                          {formData.idNumber || 'Non spécifié'}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Statut de vérification
                      </label>
                      
                      {isEditing ? (
                        <select 
                          value={formData.idStatus || ''} 
                          onChange={(e) => handleFieldChange('idStatus', e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#197ca8] focus:ring-[#197ca8] sm:text-sm"
                        >
                          <option value="">Non spécifié</option>
                          <option value="pending">En attente</option>
                          <option value="verified">Vérifié</option>
                          <option value="rejected">Rejeté</option>
                        </select>
                      ) : (
                        <div className="mt-1">
                          <IdStatusBadge status={formData.idStatus} />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Informations importantes</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• En tant qu'administrateur, votre identité doit être vérifiée</li>
                      <li>• Vous êtes responsable de la gestion du compte organisationnel</li>
                      <li>• Vos informations sont liées aux profils d'entreprise et d'institution</li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </PageContainer>
  );
}